/**
 * Countdown — pure decision layer (hex: domain in lib/, render in the component).
 * Given the milliseconds remaining, decides WHICH three units to show and the
 * urgency TIER. No `Date.now()` here so it stays pure and unit-testable; the
 * client `<Countdown>` feeds it `endsAt - now`.
 *
 * DA (mockup C12): one countdown, 4 formats by magnitude (Mo/D/H · D/H/M ·
 * H/M/S · M/S/Ms — always the 3 most significant units, tabular figures), and
 * an urgency escalation that stays WARM, never loud red:
 *   neutral (> 1h) → heating amber (≤ 1h, "ça chauffe") → ember-dark (final
 *   minute, ms shown). See .tondocs/ui/FOIL.md ("heat is never foil").
 */

export type CountdownTier = "neutral" | "heating" | "final";

export type CountdownUnitKey = "mo" | "d" | "h" | "m" | "s" | "ms";

export interface CountdownUnit {
  key: CountdownUnitKey;
  value: number;
}

export interface CountdownView {
  tier: CountdownTier;
  units: CountdownUnit[];
  totalMs: number;
  expired: boolean;
}

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const MONTH = 30 * DAY;

/** Urgency tier by time left. Warm escalation only — never a loud-red tier. */
export function getCountdownTier(totalMs: number): CountdownTier {
  if (totalMs <= MINUTE) return "final";
  if (totalMs <= HOUR) return "heating";
  return "neutral";
}

export function getCountdownView(msRemaining: number): CountdownView {
  const totalMs = Math.max(0, msRemaining);
  const expired = totalMs <= 0;
  const tier = getCountdownTier(totalMs);

  const months = Math.floor(totalMs / MONTH);
  const days = Math.floor((totalMs % MONTH) / DAY);
  const hours = Math.floor((totalMs % DAY) / HOUR);
  const minutes = Math.floor((totalMs % HOUR) / MINUTE);
  const seconds = Math.floor((totalMs % MINUTE) / SECOND);
  // Centiseconds (0–99) for a 2-digit ms display in the final minute.
  const centis = Math.floor((totalMs % SECOND) / 10);

  let units: CountdownUnit[];
  if (months > 0) {
    units = [
      { key: "mo", value: months },
      { key: "d", value: days },
      { key: "h", value: hours },
    ];
  } else if (days > 0) {
    units = [
      { key: "d", value: days },
      { key: "h", value: hours },
      { key: "m", value: minutes },
    ];
  } else if (hours > 0) {
    units = [
      { key: "h", value: hours },
      { key: "m", value: minutes },
      { key: "s", value: seconds },
    ];
  } else {
    units = [
      { key: "m", value: minutes },
      { key: "s", value: seconds },
    ];
    if (tier === "final") units.push({ key: "ms", value: centis });
  }

  return { tier, units, totalMs, expired };
}
